import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import AssignmentAuthoringProvider, {
  useAssignmentAuthoring,
} from "./AssignmentAuthoringProvider";
import SectionList from "./SectionList";
import AddSectionDialog from "./AddSectionDialog";
import AssignmentSidebar from "./AssignmentSidebar";
import AssignmentPreviewDialog from "./AssignmentPreviewDialog";

const AssignmentAuthoringContent = () => {
  const {
    sections,
    addSection,
    isAutosaving,
    validationErrors,
  } = useAssignmentAuthoring();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [previewSectionId, setPreviewSectionId] = useState(null);
  const [assignmentPreviewOpen, setAssignmentPreviewOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(null);

  const sectionsErrorMessage = useMemo(() => {
    if (!validationErrors) {
      return null;
    }

    if (typeof validationErrors === "string") {
      return validationErrors;
    }

    if (validationErrors.sections) {
      const { sections } = validationErrors;
      if (typeof sections === "string") {
        return sections;
      }

      if (Array.isArray(sections)) {
        return sections.filter(Boolean).join(" ");
      }
    }

    return null;
  }, [validationErrors]);

  const handleTemplateSelect = (section) => {
    addSection(section);
    setDialogOpen(false);
  };

  useEffect(() => {
    if (!Array.isArray(sections) || sections.length === 0) {
      setActiveSectionId(null);
      return;
    }

    setActiveSectionId((prevActive) => {
      if (prevActive && sections.some((section) => section.id === prevActive)) {
        return prevActive;
      }

      return sections[sections.length - 1].id;
    });
  }, [sections]);

  useEffect(() => {
    setCollapsedSections((prev) => {
      const sectionIds = new Set(sections.map((section) => section.id));
      const filteredEntries = Object.entries(prev || {}).filter(([key]) =>
        sectionIds.has(key)
      );
      if (filteredEntries.length === Object.keys(prev || {}).length) {
        return prev;
      }

      return Object.fromEntries(filteredEntries);
    });
  }, [sections]);

  const handleToggleSectionCollapse = useCallback((sectionId) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev?.[sectionId],
    }));
  }, []);

  const handleSelectSection = useCallback((sectionId) => {
    if (!sectionId) {
      return;
    }

    setActiveSectionId(sectionId);
    setCollapsedSections((prev) => {
      if (!prev?.[sectionId]) {
        return prev;
      }

      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
  }, []);

  const handlePreviewSection = useCallback((sectionId) => {
    setPreviewSectionId(sectionId);
    setAssignmentPreviewOpen(false);
  }, []);

  const handlePreviewAssignment = useCallback(() => {
    setAssignmentPreviewOpen(true);
    setPreviewSectionId(null);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewSectionId(null);
    setAssignmentPreviewOpen(false);
  }, []);

  return (
    <Stack spacing={3} data-testid="assignment-authoring-shell">
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }} gutterBottom>
          Assignment builder
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Arrange the sections below to structure your assignment. Drag the handle to reorder sections or duplicate existing content.
        </Typography>
      </Box>
      {sectionsErrorMessage && <Alert severity="error">{sectionsErrorMessage}</Alert>}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: "flex-start",
          gap: 3,
        }}
      >
        <AssignmentSidebar
          sections={sections}
          collapsedSections={collapsedSections}
          onToggleSection={handleToggleSectionCollapse}
          onPreviewSection={handlePreviewSection}
          onPreviewAssignment={handlePreviewAssignment}
          onAddSection={() => setDialogOpen(true)}
          onSelectSection={handleSelectSection}
          activeSectionId={activeSectionId}
        />
        <Box sx={{ flex: 1, width: "100%" }}>
          <SectionList
            onAddSection={() => setDialogOpen(true)}
            collapsedSections={collapsedSections}
            onToggleSectionCollapse={handleToggleSectionCollapse}
            onPreviewSection={handlePreviewSection}
            activeSectionId={activeSectionId}
          />
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary">
        {isAutosaving ? "Autosaving changes…" : `${sections.length} section${sections.length === 1 ? "" : "s"} configured.`}
      </Typography>
      <AddSectionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={handleTemplateSelect}
      />
      <AssignmentPreviewDialog
        open={assignmentPreviewOpen || Boolean(previewSectionId)}
        sections={sections}
        sectionId={previewSectionId}
        onClose={handleClosePreview}
        showAssignmentPreview={assignmentPreviewOpen}
      />
    </Stack>
  );
};

const AssignmentAuthoringShell = ({ initialSections = [], children }) => (
  <AssignmentAuthoringProvider initialSections={initialSections}>
    <AssignmentAuthoringContent />
    {children}
  </AssignmentAuthoringProvider>
);

export default AssignmentAuthoringShell;
